import { useUnsavedPromptInModal } from '@common/UnsavedPromptInModal.hooks';
import { useFetchCompanyTypes } from '@company/companyType/companyType.queries';
import { useFetchOrganizationTypes } from '@company/organizationType/organizationType.queries';
import { IProjectCompany } from '@company/projectCompany/projectCompany.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { Button, ComponentType, IOption, LoadingButton, ModalBodyExtras, useModal } from '@gcl/react-core';
import { ICompanyType } from '@company/companyType/companyType.models';
import { AxiosErrorResponse } from '@core/error.models';
import { UseQueryResult } from '@tanstack/react-query';
import { IOrganizationTypeOption } from '@company/organizationType/organizationType.models';
import { CompanyForm, ICompanyFormProps } from './CompanyForm';

export interface IUseCompanyModalProps {
  projectId?: number;
  onConfirm: (company?: IProjectCompany) => void;
}

export interface IRenderCompanyModalProps
  extends Omit<ICompanyFormProps, 'companyTypeOptions' | 'organizationOptions' | 'setIsFormDirty'> {
  isSubmitLoading?: boolean;
  isSubmitError?: boolean;
  isSubmitSuccess?: boolean;
}

export const useCompanyModal = ({ projectId, onConfirm }: IUseCompanyModalProps) => {
  const { openPortal, isOpen, closePortal, renderModal } = useModal();

  const renderCompanyModal = ({
    company,
    isSubmitLoading,
    isSubmitError,
    isSubmitSuccess,
    ...companyFormProps
  }: IRenderCompanyModalProps) => {
    const { useFormMethods } = companyFormProps;
    const {
      formState: { isDirty },
    } = useFormMethods;

    const { isFormDirty, setIsFormDirty, confirmUnsavedPrompt } = useUnsavedPromptInModal();
    const { data: companyTypeOptions } = (
      useFetchCompanyTypes({
        isDirty,
      }) as () => UseQueryResult<IOption<ICompanyType>[], AxiosErrorResponse>
    )();
    const { data: organizationTypeOptions } = (
      useFetchOrganizationTypes({
        isDirty,
      }) as () => UseQueryResult<IOption<IOrganizationTypeOption>[], AxiosErrorResponse>
    )();

    return (
      isOpen &&
      renderModal({
        onHide: () => {
          useFormMethods.reset({});
        },
        backdrop: isFormDirty ? 'static' : undefined,
        large: true,
        title: `${company && isPositiveNumber(company.id) ? 'Edit' : 'Add'} Company`,
        onRenderBody: (bodyExtras?: ModalBodyExtras) => {
          return (
            <CompanyForm
              setIsFormDirty={setIsFormDirty}
              company={company}
              companyTypeOptions={companyTypeOptions}
              organizationTypeOptions={organizationTypeOptions}
              {...companyFormProps}
            />
          );
        },
        onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
          const onClickCancel = () => {
            if (confirmUnsavedPrompt() && onHide) {
              onHide();
            }
          };
          return (
            <>
              <LoadingButton
                type={ComponentType.Primary}
                onClick={() => {
                  onConfirm(company);
                }}
                isLoading={isSubmitLoading ?? false}
                isError={isSubmitError}
                isSuccess={isSubmitSuccess}
                timeToResetSuccessState={3000}
                timeToResetErrorState={5000}
                prefixIconName="FolderCheck"
              >
                Save
              </LoadingButton>
              <Button
                prefixIconName="ChevronLeft"
                type={ComponentType.Secondary}
                onClick={onClickCancel}
                style={{ margin: '10px' }}
              >
                Cancel
              </Button>
            </>
          );
        },
      })
    );
  };
  return {
    openPortal,
    closePortal,
    renderModal: renderCompanyModal,
  };
};
